import React, { useState } from 'react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import useContact from '../hooks/useContact';
import { parseApiError } from '../utils/apiError';

const EMPTY = { email: '', subject: '', body: '' };

// Server rejects a message body over 300 chars (counted after trimming, by
// UTF-16 code units — same as JS String.length). Mirror that here so the user
// never trips the 400; the server error mapping stays as a backstop.
const MAX_BODY = 300;

// Contact Us — posts to the public /api/emailer/contact_us endpoint
// (same contract the legacy site used), with the v2 form UX.
export default function Contact() {
  const { send, loading } = useContact();
  const { addToast } = useToast();
  const [values, setValues] = useState(EMPTY);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [sent, setSent] = useState(false);

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  // Count the trimmed body, matching the server's rule exactly.
  const bodyLen = values.body.trim().length;
  const bodyOver = bodyLen > MAX_BODY;

  // Mirror the legacy "enabled only when all three are filled" behavior.
  const isValid = values.email.trim() && values.subject.trim() && values.body.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    try {
      await send(values);
      setSent(true);
      setValues(EMPTY);
      addToast('Message sent — thank you!', { variant: 'success' });
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe || 'Sorry — we couldn’t send your message. Please try again.');
      setFieldErrors(fld);
    }
  };

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)', maxWidth: 640 }}>
      <h1>Contact us</h1>
      <p style={{ marginTop: 'var(--ss-space-3)' }}>
        Have a special request, or a question about one of our products? You’ve
        come to the right place.
      </p>

      <Card style={{ marginTop: 'var(--ss-space-5)' }}>
        {sent ? (
          <div className="ss-form__success" role="status">
            <p>Thanks for reaching out — your message is on its way. We’ll be in touch soon.</p>
            <p style={{ marginTop: 'var(--ss-space-3)' }}>
              <Button variant="ghost" size="sm" onClick={() => setSent(false)}>Send another message</Button>
            </p>
          </div>
        ) : (
          <form className="ss-form" onSubmit={onSubmit} noValidate>
            {formError && <p className="ss-form__error" role="alert">{formError}</p>}

            <Input
              label="Your email"
              type="email"
              value={values.email}
              onChange={setField('email')}
              error={fieldErrors.email}
              autoComplete="email"
              required
            />
            <Input
              label="Subject"
              value={values.subject}
              onChange={setField('subject')}
              error={fieldErrors.subject}
              required
            />

            <div className="ss-field">
              <div className="ss-label-row">
                <label htmlFor="contact-body" className="ss-label">Message</label>
                <span
                  id="contact-body-count"
                  className={`ss-char-count ${bodyOver ? 'ss-char-count--over' : ''}`.trim()}
                  aria-live="polite"
                >
                  {bodyLen}/{MAX_BODY}
                </span>
              </div>
              <textarea
                id="contact-body"
                className={`ss-input ${fieldErrors.body || bodyOver ? 'ss-input--error' : ''}`.trim()}
                rows={6}
                value={values.body}
                onChange={setField('body')}
                aria-invalid={fieldErrors.body || bodyOver ? 'true' : undefined}
                aria-describedby="contact-body-count"
              />
              {(fieldErrors.body || bodyOver) && (
                <p className="ss-field__error">
                  {fieldErrors.body || `Message must be ${MAX_BODY} characters or fewer.`}
                </p>
              )}
            </div>

            <div className="ss-form__actions">
              <Button type="submit" disabled={!isValid || bodyOver || loading}>
                {loading ? <Spinner label="Sending" /> : 'Send message'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
